import React, { useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import toast from 'react-hot-toast';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0 = login, 1 = reset password
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setError('Please enter your email');
      return;
    }
    setLoading(true);
    setError('');
    setNewPassword('');
    try {
      // Find user by email and reset password
      const res = await api.post('/auth/reset-password', { email: resetEmail });
      if (res.data.password) {
        setNewPassword(res.data.password);
        navigator.clipboard.writeText(res.data.password);
        toast.success('New password generated and copied to clipboard!');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Tabs value={tab} onChange={(e, v) => { setTab(v); setError(''); setNewPassword(''); }} sx={{ mb: 3 }}>
          <Tab label="Sign In" />
          <Tab label="Forgot Password" />
        </Tabs>

        {tab === 0 ? (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ color: '#2c3e50' }}>🔐 Sign In</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Sign in to access your projects</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleLogin}>
              <TextField fullWidth label="Email" type="email" margin="normal" value={email} onChange={(e) => setEmail(e.target.value)} required size="small" />
              <TextField fullWidth label="Password" type="password" margin="normal" value={password} onChange={(e) => setPassword(e.target.value)} required size="small" />
              <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ mt: 3, bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </>
        ) : (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ color: '#2c3e50' }}>🔑 Reset Password</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Enter your email to generate a new password</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {newPassword && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>New Password:</strong> <code style={{ background: '#e8f8f5', padding: '4px 8px', borderRadius: '4px', fontSize: '16px' }}>{newPassword}</code>
                <br />Copied to clipboard! Use it to sign in.
              </Alert>
            )}
            <form onSubmit={handleResetPassword}>
              <TextField fullWidth label="Your Email" type="email" margin="normal" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required size="small" />
              <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ mt: 3, bgcolor: '#e67e22', '&:hover': { bgcolor: '#d35400' } }}>
                {loading ? 'Generating...' : 'Generate New Password'}
              </Button>
            </form>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoginPage;
